import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import {
  Wallet, Plus, Edit, Trash2, Banknote, Smartphone, CreditCard, ArrowLeftRight,
  Paperclip, X, Download, FileSpreadsheet, FileText, ChevronDown, Filter, RotateCw, RefreshCw,
  Upload, CheckCircle2, AlertCircle,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";

const PAY_METHODS = [
  { value: "efectivo", label: "Efectivo", Icon: Banknote, color: "text-emerald-600 bg-emerald-50" },
  { value: "sinpe", label: "SINPE Móvil", Icon: Smartphone, color: "text-indigo-600 bg-indigo-50" },
  { value: "transferencia", label: "Transferencia", Icon: ArrowLeftRight, color: "text-blue-600 bg-blue-50" },
  { value: "tarjeta", label: "Tarjeta", Icon: CreditCard, color: "text-slate-700 bg-slate-100" },
];

const PAY_META = Object.fromEntries(PAY_METHODS.map((p) => [p.value, p]));

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyExpense = {
  fecha: todayISO(),
  descripcion: "",
  monto: "",
  moneda: "CRC",
  metodo_pago: "efectivo",
  categoria: "Otros",
  proveedor_id: "",
  proveedor_nombre: "",
  empleado_id: "",
  empleado_nombre: "",
  notas: "",
  repetir_mensual: false,
};

const fmt = (n, cur) =>
  new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: cur || "CRC",
    maximumFractionDigits: (cur || "CRC") === "USD" ? 2 : 0,
  }).format(Number(n) || 0);

export default function Expenses() {
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [me, setMe] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyExpense);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);

  // Import dialog state — wraps GET /expenses/template[/excel] + POST /expenses/import.
  // `importResult` holds the backend response after upload so the user
  // sees inserted/skipped counts + per-row errors before closing.
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Filters
  const location = useLocation();
  const [fCategoria, setFCategoria] = useState("");
  const [fMetodo, setFMetodo] = useState("");
  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");
  const [fEmpleado, setFEmpleado] = useState("");
  const [fSoloRecurrentes, setFSoloRecurrentes] = useState(
    Boolean(location.state?.filterRecurring),
  );
  // Trend
  const [trend, setTrend] = useState([]);

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (fCategoria) p.set("categoria", fCategoria);
    if (fMetodo) p.set("metodo_pago", fMetodo);
    if (fDesde) p.set("desde", fDesde);
    if (fHasta) p.set("hasta", fHasta);
    if (fEmpleado) p.set("empleado_id", fEmpleado);
    if (fSoloRecurrentes) p.set("solo_recurrentes", "true");
    return p.toString();
  };

  const fetchExpenses = async () => {
    const qs = buildQuery();
    const res = await api.get(`/expenses${qs ? `?${qs}` : ""}`);
    setExpenses(res.data || []);
  };

  const fetchAll = async () => {
    try {
      const [exp, cat, sup, emp, meRes, tr] = await Promise.all([
        api.get(`/expenses${buildQuery() ? `?${buildQuery()}` : ""}`),
        api.get("/expenses/categories"),
        api.get("/suppliers").catch(() => ({ data: [] })),
        api.get("/employees").catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: null })),
        api.get("/expenses/trend?days=30").catch(() => ({ data: { series: [] } })),
      ]);
      setExpenses(exp.data || []);
      setCategories(cat.data?.categorias || []);
      setSuppliers(sup.data || []);
      setEmployees(emp.data || []);
      setMe(meRes.data);
      setTrend(tr.data?.series || []);
    } catch {
      toast.error("Error al cargar gastos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch list when filters change (after initial load)
  useEffect(() => {
    if (loading) return;
    fetchExpenses().catch(() => toast.error("Error al filtrar"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fCategoria, fMetodo, fDesde, fHasta, fEmpleado, fSoloRecurrentes]);

  const clearFilters = () => {
    setFCategoria("");
    setFMetodo("");
    setFDesde("");
    setFHasta("");
    setFEmpleado("");
    setFSoloRecurrentes(false);
  };
  const hasFilters = Boolean(fCategoria || fMetodo || fDesde || fHasta || fEmpleado || fSoloRecurrentes);

  const downloadExport = async (format) => {
    try {
      const token = localStorage.getItem("token");
      const qs = buildQuery();
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/expenses/export/${format}${qs ? `?${qs}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const ext = format === "excel" ? "xlsx" : format;
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.setAttribute("download", `gastos.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);
      toast.success(`Gastos exportados (${ext.toUpperCase()})`);
    } catch {
      toast.error("Error al exportar");
    }
  };

  /** Download the bulk-import template — either CSV (universal, opens in
   *  Excel/Numbers/Sheets) or XLSX with dropdowns + frozen header. */
  const downloadTemplate = async (format = "csv") => {
    try {
      const token = localStorage.getItem("token");
      const suffix = format === "excel" ? "/excel" : "";
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/expenses/template${suffix}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("template failed");
      const blob = await res.blob();
      const ext = format === "excel" ? "xlsx" : "csv";
      const objUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objUrl;
      link.setAttribute("download", `plantilla_gastos.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objUrl);
    } catch {
      toast.error("Error descargando plantilla");
    }
  };

  /** Upload the chosen file. Backend never aborts on bad rows: it returns
   *  `{ imported, skipped, errors, rows_total }` so the dialog can show
   *  a per-row report before the user closes. */
  const submitImport = async () => {
    if (!importFile) {
      toast.error("Elegí un archivo (.csv o .xlsx)");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/expenses/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      if (data.imported > 0) {
        toast.success(`${data.imported} gasto(s) importado(s)`);
        await fetchAll();   // refresh the list in the background
      }
      if (data.skipped > 0) {
        toast.warning(`${data.skipped} fila(s) con errores — revisalas abajo`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportResult(null);
  };


  const openNew = () => {
    setEditing(null);
    setForm(emptyExpense);
    setReceiptFile(null);
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      fecha: e.fecha || todayISO(),
      descripcion: e.descripcion || "",
      monto: String(e.monto ?? ""),
      moneda: e.moneda || "CRC",
      metodo_pago: e.metodo_pago || "efectivo",
      categoria: e.categoria || "Otros",
      proveedor_id: e.proveedor_id || "",
      proveedor_nombre: e.proveedor_nombre || "",
      empleado_id: e.empleado_id || "",
      empleado_nombre: e.empleado_nombre || "",
      notas: e.notas || "",
      repetir_mensual: !!e.repetir_mensual,
    });
    setReceiptFile(null);
    setDialogOpen(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.descripcion.trim()) {
      toast.error("La descripción es obligatoria");
      return;
    }
    const monto = parseFloat(form.monto);
    if (Number.isNaN(monto) || monto < 0) {
      toast.error("El monto debe ser un número positivo");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, monto };
      let res;
      if (editing) {
        res = await api.put(`/expenses/${editing.id}`, payload);
        toast.success("Gasto actualizado");
      } else {
        res = await api.post("/expenses", payload);
        toast.success("Gasto registrado");
      }
      // Optional receipt upload
      if (receiptFile && res.data?.id) {
        try {
          const fd = new FormData();
          fd.append("file", receiptFile);
          await api.post(`/expenses/${res.data.id}/receipt`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success("Recibo adjuntado");
        } catch {
          toast.error("El gasto se guardó pero falló subir el recibo");
        }
      }
      setDialogOpen(false);
      setReceiptFile(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/expenses/${deleteConfirm.id}`);
      toast.success("Gasto eliminado");
      setDeleteConfirm(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  const downloadReceipt = (id) => {
    const token = localStorage.getItem("token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/expenses/${id}/receipt?auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  };

  // Quick aggregates shown above the table
  const totals = useMemo(() => {
    const t = { CRC: 0, USD: 0, count: expenses.length };
    expenses.forEach((e) => {
      const cur = (e.moneda || "CRC").toUpperCase();
      t[cur] = (t[cur] || 0) + Number(e.monto || 0);
    });
    return t;
  }, [expenses]);

  if (loading) return <PageLoader label="Cargando gastos…" testid="expenses-loader" />;

  const isSeller = me?.role === "seller";
  const canEdit = (e) => {
    if (!me) return false;
    if (me.role === "admin" && e.user_id === me.id) return true;
    if (me.role === "seller" && e.created_by_id === me.id) return true;
    return false;
  };

  return (
    <div data-testid="expenses-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Gastos
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {isSeller ? "Tus gastos de operación" : "Todos los gastos de la empresa"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="export-expenses-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar
                <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem
                onClick={() => downloadExport("excel")}
                className="rounded-sm cursor-pointer"
                data-testid="export-expenses-excel"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => downloadExport("pdf")}
                className="rounded-sm cursor-pointer"
                data-testid="export-expenses-pdf"
              >
                <FileText className="h-4 w-4 mr-2 text-rose-600" /> PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            onClick={() => setImportOpen(true)}
            variant="outline"
            className="rounded-sm"
            data-testid="open-expenses-import"
          >
            <Upload className="h-4 w-4 mr-2" />
            Importar
          </Button>
          <Button onClick={openNew} className="btn-primary rounded-sm" data-testid="add-expense-button">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Gasto
          </Button>
        </div>
      </div>

      {/* Daily trend chart (last 30 days) */}
      <Card className="bg-white border border-slate-200 rounded-sm p-5 mb-4" data-testid="expenses-trend-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700" style={{ fontFamily: "Cabinet Grotesk" }}>
              Tendencia diaria · 30 días
            </h3>
            <p className="text-[11px] text-slate-500">Barras apiladas por moneda</p>
          </div>
        </div>
        <div style={{ width: "100%", height: 180 }}>
          <ResponsiveContainer>
            <BarChart data={trend} margin={{ top: 5, right: 8, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis
                dataKey="fecha"
                tick={{ fontSize: 10, fill: "#64748B" }}
                tickFormatter={(v) => v?.slice(5) || ""}
                interval={4}
              />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} width={60} />
              <Tooltip
                cursor={{ fill: "#F1F5F9" }}
                formatter={(v, name) => [fmt(v, name), name]}
                labelFormatter={(l) => `Fecha: ${l}`}
                contentStyle={{ fontSize: 12, borderRadius: 4, border: "1px solid #E2E8F0" }}
              />
              <Bar dataKey="CRC" stackId="m" fill="#002FA7" radius={[2, 2, 0, 0]} />
              <Bar dataKey="USD" stackId="m" fill="#10B981" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Filter bar */}
      <Card className="bg-white border border-slate-200 rounded-sm p-4 mb-4" data-testid="expenses-filters">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Filter className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">Filtros</h3>
          <button
            type="button"
            onClick={() => setFSoloRecurrentes(!fSoloRecurrentes)}
            className={`text-[11px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm transition-colors flex items-center gap-1 ${
              fSoloRecurrentes
                ? "bg-indigo-600 text-white"
                : "bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
            }`}
            data-testid="filter-only-recurring"
          >
            <RefreshCw className="h-3 w-3" />
            {fSoloRecurrentes ? "Solo recurrentes ✓" : "Solo recurrentes"}
          </button>
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-auto text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
              data-testid="clear-filters-button"
            >
              <RotateCw className="h-3 w-3" />
              Limpiar
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Categoría</label>
            <Select
              value={fCategoria || "_all_"}
              onValueChange={(v) => setFCategoria(v === "_all_" ? "" : v)}
            >
              <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="filter-categoria-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">Todas</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Método de pago</label>
            <Select
              value={fMetodo || "_all_"}
              onValueChange={(v) => setFMetodo(v === "_all_" ? "" : v)}
            >
              <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="filter-metodo-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all_">Todos</SelectItem>
                {PAY_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {!isSeller && (
            <div>
              <label className="block text-[11px] text-slate-500 mb-1">Colaborador</label>
              <Select
                value={fEmpleado || "_all_"}
                onValueChange={(v) => setFEmpleado(v === "_all_" ? "" : v)}
              >
                <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="filter-empleado-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all_">Todos</SelectItem>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.nombre_completo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Desde</label>
            <Input
              type="date"
              value={fDesde}
              onChange={(e) => setFDesde(e.target.value)}
              className="rounded-sm h-9 text-xs"
              data-testid="filter-desde-input"
            />
          </div>
          <div>
            <label className="block text-[11px] text-slate-500 mb-1">Hasta</label>
            <Input
              type="date"
              value={fHasta}
              onChange={(e) => setFHasta(e.target.value)}
              className="rounded-sm h-9 text-xs"
              data-testid="filter-hasta-input"
            />
          </div>
        </div>
      </Card>

      {/* Totals row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="expenses-totals">
        <Card className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Total CRC</div>
          <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Cabinet Grotesk" }}>
            {fmt(totals.CRC, "CRC")}
          </div>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Total USD</div>
          <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Cabinet Grotesk" }}>
            {fmt(totals.USD, "USD")}
          </div>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Movimientos</div>
          <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Cabinet Grotesk" }}>
            {totals.count}
          </div>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {expenses.length === 0 ? (
          <EmptyState
            icon={Wallet}
            title="Sin gastos registrados"
            description="Registrá tu primer gasto: combustible, alquiler, comisiones bancarias, lo que sea."
            actionLabel="Nuevo Gasto"
            onAction={openNew}
            testid="expenses-empty-state"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Fecha</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Descripción</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Categoría</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Pago</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-600">Proveedor</th>
                  {!isSeller && (
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Colaborador</th>
                  )}
                  {!isSeller && (
                    <th className="text-left py-3 px-4 font-medium text-slate-600">Registró</th>
                  )}
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Monto</th>
                  <th className="text-right py-3 px-4 font-medium text-slate-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => {
                  const pay = PAY_META[e.metodo_pago] || PAY_META.efectivo;
                  const Icon = pay.Icon;
                  return (
                    <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid="expense-row">
                      <td className="py-3 px-4 mono text-xs text-slate-600">{e.fecha}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">{e.descripcion}</span>
                          {e.repetir_mensual && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-indigo-50 text-indigo-700 border border-indigo-200"
                              title="Gasto recurrente: se crea solo cada mes"
                              data-testid={`recurring-badge-${e.id}`}
                            >
                              <RefreshCw className="h-2.5 w-2.5" />
                              Mensual
                            </span>
                          )}
                        </div>
                        {e.notas && <div className="text-[11px] text-slate-500 truncate max-w-xs">{e.notas}</div>}
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-xs px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700 border border-slate-200">
                          {e.categoria}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-1.5 py-0.5 rounded-sm ${pay.color}`}>
                          <Icon className="h-3 w-3" />
                          {pay.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-600 text-xs">{e.proveedor_nombre || "—"}</td>
                      {!isSeller && (
                        <td className="py-3 px-4 text-xs">
                          {e.empleado_nombre ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-200">
                              {e.empleado_nombre}
                            </span>
                          ) : "—"}
                        </td>
                      )}
                      {!isSeller && (
                        <td className="py-3 px-4 text-xs">
                          <span className="mono text-slate-600">{e.created_by_name || "—"}</span>
                        </td>
                      )}
                      <td className="py-3 px-4 text-right">
                        <span className="font-semibold mono text-rose-600">
                          −{fmt(e.monto, e.moneda)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          {e.recibo_storage_path && (
                            <Button
                              onClick={() => downloadReceipt(e.id)}
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              data-testid={`download-receipt-${e.id}`}
                              title="Descargar recibo"
                            >
                              <Paperclip className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {canEdit(e) && (
                            <>
                              <Button
                                onClick={() => openEdit(e)}
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                data-testid={`edit-expense-${e.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                onClick={() => setDeleteConfirm(e)}
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                                data-testid={`delete-expense-${e.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Expense Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Gasto" : "Nuevo Gasto"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1.5">Descripción *</label>
                <Input
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  required
                  className="rounded-sm"
                  placeholder="Ej: Combustible camión 1"
                  data-testid="expense-descripcion-input"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Monto *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.monto}
                  onChange={(e) => setForm({ ...form, monto: e.target.value })}
                  required
                  className="rounded-sm mono"
                  placeholder="0"
                  data-testid="expense-monto-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Moneda</label>
                <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="expense-moneda-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CRC">CRC · Colones</SelectItem>
                    <SelectItem value="USD">USD · Dólares</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Método de pago</label>
                <Select value={form.metodo_pago} onValueChange={(v) => setForm({ ...form, metodo_pago: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="expense-metodo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAY_METHODS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Categoría</label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="expense-categoria-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Fecha</label>
                <Input
                  type="date"
                  value={form.fecha}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className="rounded-sm"
                  data-testid="expense-fecha-input"
                />
              </div>
              {!isSeller && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1.5">Proveedor (opcional)</label>
                  <Select
                    value={form.proveedor_id || "_none_"}
                    onValueChange={(v) => {
                      if (v === "_none_") {
                        setForm({ ...form, proveedor_id: "", proveedor_nombre: "" });
                      } else {
                        const s = suppliers.find((sp) => sp.id === v);
                        setForm({
                          ...form,
                          proveedor_id: v,
                          proveedor_nombre: s?.nombre || s?.nombre_proveedor || "",
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="expense-proveedor-select">
                      <SelectValue placeholder="— Sin proveedor —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">— Sin proveedor —</SelectItem>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.nombre || s.nombre_proveedor}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!isSeller && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1.5">Colaborador asignado (opcional)</label>
                  <Select
                    value={form.empleado_id || "_none_"}
                    onValueChange={(v) => {
                      if (v === "_none_") {
                        setForm({ ...form, empleado_id: "", empleado_nombre: "" });
                      } else {
                        const emp = employees.find((e) => e.id === v);
                        setForm({
                          ...form,
                          empleado_id: v,
                          empleado_nombre: emp?.nombre_completo || "",
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="expense-empleado-select">
                      <SelectValue placeholder="— Sin colaborador —" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">— Sin colaborador —</SelectItem>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.nombre_completo}
                          {emp.puesto ? <span className="text-slate-400"> · {emp.puesto}</span> : null}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Útil para reembolsos, viáticos o gastos asociados a un vendedor/empleado específico.
                  </p>
                </div>
              )}
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1.5">Notas</label>
                <Textarea
                  value={form.notas}
                  onChange={(e) => setForm({ ...form, notas: e.target.value })}
                  className="rounded-sm"
                  rows={2}
                  placeholder="Comentarios opcionales…"
                  data-testid="expense-notas-input"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1.5">Recibo (opcional)</label>
                {receiptFile ? (
                  <div className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded-sm text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{receiptFile.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReceiptFile(null)}
                      className="text-red-600 hover:bg-red-50 p-1 rounded-sm"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                    onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                    className="rounded-sm text-xs file:mr-2 file:px-2 file:py-1 file:rounded-sm file:border-0 file:bg-slate-100 file:text-slate-700"
                    data-testid="expense-receipt-input"
                  />
                )}
                <p className="text-[11px] text-slate-500 mt-1">PDF, JPG, PNG, WebP o HEIC · máx 10MB</p>
              </div>
              {/* Recurrence toggle */}
              <div className="col-span-2 pt-2 border-t border-slate-200" data-testid="expense-recurrence-section">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="h-4 w-4 text-[rgb(0,47,167)]" />
                      <label className="text-sm font-semibold text-slate-700">Repetir cada mes</label>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Activá esto para gastos fijos (alquiler, salarios, internet). El sistema
                      creará una copia automáticamente cada mes en el mismo día.
                    </p>
                  </div>
                  <Switch
                    checked={!!form.repetir_mensual}
                    onCheckedChange={(v) => setForm({ ...form, repetir_mensual: v })}
                    data-testid="expense-recurrence-switch"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                Cancelar
              </Button>
              <Button type="submit" disabled={submitting} className="btn-primary rounded-sm" data-testid="save-expense-button">
                {submitting ? "Guardando…" : editing ? "Actualizar" : "Registrar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el gasto "{deleteConfirm?.descripcion}" por {deleteConfirm && fmt(deleteConfirm.monto, deleteConfirm.moneda)}.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Importar gastos desde Excel/CSV — template + upload + per-row report */}
      <Dialog
        open={importOpen}
        onOpenChange={(o) => {
          if (importing) return;
          setImportOpen(o);
          if (!o) resetImport();
        }}
      >
        <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="expenses-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[rgb(0,47,167)]" />
              Importar gastos
            </DialogTitle>
          </DialogHeader>

          {!importResult && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <p className="text-sm font-semibold text-slate-900 mb-2">
                  1. Descargá la plantilla
                </p>
                <p className="text-xs text-slate-600 mb-3">
                  La plantilla ya trae las columnas correctas + dos filas de ejemplo. Borrá los ejemplos antes de importar.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => downloadTemplate("excel")}
                    className="rounded-sm"
                    data-testid="expenses-template-excel"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" />
                    Plantilla Excel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => downloadTemplate("csv")}
                    className="rounded-sm"
                    data-testid="expenses-template-csv"
                  >
                    <FileText className="h-4 w-4 mr-1.5 text-slate-600" />
                    Plantilla CSV
                  </Button>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                <p className="text-sm font-semibold text-slate-900 mb-2">
                  2. Subí tu archivo lleno
                </p>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xlsm"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:rounded-sm file:text-xs file:font-semibold file:bg-[rgb(0,47,167)] file:text-white hover:file:bg-[rgb(0,47,167)]/90"
                  data-testid="expenses-import-file"
                />
                {importFile && (
                  <p className="mt-2 text-xs text-slate-600">
                    <span className="font-bold">{importFile.name}</span> · {Math.round(importFile.size / 1024)} KB
                  </p>
                )}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
                <strong>Campos obligatorios:</strong> fecha, descripcion, monto.
                Las filas con datos faltantes o inválidos NO bloquean al resto — vas a ver un reporte detallado.
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setImportOpen(false)} className="rounded-sm" disabled={importing}>
                  Cancelar
                </Button>
                <Button
                  onClick={submitImport}
                  disabled={!importFile || importing}
                  className="btn-primary rounded-sm"
                  data-testid="expenses-import-submit"
                >
                  {importing ? "Importando…" : "Importar gastos"}
                </Button>
              </div>
            </div>
          )}

          {importResult && (
            <div className="space-y-3 mt-2" data-testid="expenses-import-result">
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3 text-center">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-2xl font-black text-emerald-700 mono" data-testid="expenses-import-imported-count" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                    {importResult.imported}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Importadas</p>
                </div>
                <div className="border border-amber-200 bg-amber-50 rounded-sm p-3 text-center">
                  <AlertCircle className="h-5 w-5 text-amber-600 mx-auto mb-1" />
                  <p className="text-2xl font-black text-amber-700 mono" data-testid="expenses-import-skipped-count" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                    {importResult.skipped}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">Con error</p>
                </div>
                <div className="border border-slate-200 bg-slate-50 rounded-sm p-3 text-center">
                  <p className="text-2xl font-black text-slate-700 mono" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                    {importResult.rows_total}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Total filas</p>
                </div>
              </div>

              {(importResult.errors || []).length > 0 && (
                <div className="border border-amber-200 rounded-sm overflow-hidden">
                  <div className="bg-amber-50 px-3 py-2 border-b border-amber-200">
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
                      Filas con error ({importResult.errors.length})
                    </p>
                  </div>
                  <div className="max-h-[280px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-1.5 font-bold uppercase tracking-wider text-slate-600">Fila</th>
                          <th className="text-left px-3 py-1.5 font-bold uppercase tracking-wider text-slate-600">Campo</th>
                          <th className="text-left px-3 py-1.5 font-bold uppercase tracking-wider text-slate-600">Detalle</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {importResult.errors.map((er, i) => (
                          <tr key={i} className="hover:bg-amber-50/40" data-testid={`expenses-import-error-row-${i}`}>
                            <td className="px-3 py-1.5 mono font-bold text-slate-700">{er.row}</td>
                            <td className="px-3 py-1.5 mono text-slate-600">{er.field}</td>
                            <td className="px-3 py-1.5 text-slate-700">{er.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {importResult.errors.length === 50 && (
                    <p className="px-3 py-1.5 text-[10px] italic text-slate-500 bg-slate-50 border-t border-slate-200">
                      Se muestran los primeros 50 errores. Corregí estos primero y reintentá.
                    </p>
                  )}
                </div>
              )}

              {importResult.imported > 0 && (importResult.errors || []).length === 0 && (
                <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3 text-sm text-emerald-800">
                  ¡Todo importado sin errores! Los gastos aparecen en la lista.
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={resetImport}
                  className="rounded-sm"
                  data-testid="expenses-import-again"
                >
                  Importar otro archivo
                </Button>
                <Button
                  onClick={() => setImportOpen(false)}
                  className="btn-primary rounded-sm"
                  data-testid="expenses-import-close"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
